import { useState, useMemo, useRef } from 'react';
import { InputAdornment } from '@mui/material';
import { addLiveChatEventMessage, sendUserReply } from 'actions/live-chat';
import Smooch from 'smooch';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  addScreenReaderNotification,
  selectConversationId,
  selectFreeFormInputPlaceholder,
} from 'reducers/workflows';
import {
  selectChatId,
  selectIsInAssistantMode,
  selectLiveChatIntegration,
  selectLiveChatQuery,
  selectLiveChatStatus,
  setLiveChatQuery,
} from 'reducers/live-chat';
import IntentFormWrapper from 'components/FormWrapper/IntentFormWrapper';
import { applyZopimFunction } from 'embed/zopim';
import { applyKustomerCoreFunctionWithCallback } from 'embed/kustomerCore';
import { debounce } from 'utils/debounce';
import { TextInput } from 'components/TextInput/TextInput';
import { UploadAttachmentButton } from './UploadAttachmentButton';
import { selectWidgetInitMethod } from 'reducers/config';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import { selectIsLatestStepZendeskCsat } from 'reducers/workflows/index';
import {
  sendTextMessage,
  sendTypingIndicator,
} from 'components/salesforce-messaging-one-chat/messagingService';
import { v4 as uuidv4 } from 'uuid';
import { CONVERSATION_CONSTANTS } from 'components/salesforce-messaging-one-chat/constants';
import { getErrorMessage } from 'utils/utils';
import { selectUserEndedChat } from 'reducers/live-chat/index';
import { getSmoochErrorMessage } from 'embed/smooch';

const sendKustomerTypingActivity = ({
  conversationId,
  typing,
}: {
  conversationId: string;
  typing: boolean;
}) => {
  try {
    window.KustomerCore?.sendTypingActivity?.({
      conversationId,
      typing,
    });
  } catch {
    // do nothing
  }
};

const LiveChatInput = () => {
  const dispatch = useAppDispatch();
  const [isTyping, setIsTyping] = useState(false);
  const status = useAppSelector(selectLiveChatStatus);
  const liveChatIntegration = useAppSelector(selectLiveChatIntegration);
  const chatId = useAppSelector(selectChatId);
  const isInAssistantMode = useAppSelector(selectIsInAssistantMode);
  const placeholder = useAppSelector(selectFreeFormInputPlaceholder);
  const widgetInitMethod = useAppSelector(selectWidgetInitMethod);
  const conversationId = useAppSelector(selectConversationId);
  const isLatestStepZendeskCsat = useAppSelector(selectIsLatestStepZendeskCsat);
  const userEndedChat = useAppSelector(selectUserEndedChat);
  const typingInterval = useRef<NodeJS.Timeout>();
  const liveChatQuery = useAppSelector(selectLiveChatQuery);

  const isSubmitDisabled =
    liveChatQuery.trim() === '' ||
    status !== 'active' ||
    isLatestStepZendeskCsat ||
    userEndedChat;

  const getMaxLength = () => {
    switch (liveChatIntegration) {
      case 'kustomer':
        return 10240;
      default:
        return undefined;
    }
  };

  const getMaxFileSizeMegabytes = () => {
    switch (liveChatIntegration) {
      case 'kustomer':
        return 5 * 1000 * 1000; // they say 5MB but kustomer seems to use 5,000,000 instead of 5,242,880 bytes
      case 'salesforce':
        // Salesforce docs are outdated, the actual limit is at least 10 MB, but
        // keep it at 5 MB for now to be safe.
        // https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=sendFile
        return 5 * 1024 * 1024; // 5MB
      default:
        return undefined;
    }
  };

  const debouncedEndTyping = useMemo(
    () =>
      debounce(() => {
        setIsTyping(false);

        switch (liveChatIntegration) {
          case 'kustomer':
            sendKustomerTypingActivity({
              conversationId: chatId,
              typing: false,
            });
            break;
          case 'salesforce':
            clearInterval(typingInterval.current);
            sendTypingIndicator(
              chatId,
              CONVERSATION_CONSTANTS.EntryTypes.TYPING_STOPPED_INDICATOR,
            );
            break;
        }
      }, 3000),
    [chatId, liveChatIntegration],
  );

  const startTyping = () => {
    setIsTyping(true);

    switch (liveChatIntegration) {
      case 'kustomer':
        sendKustomerTypingActivity({
          conversationId: chatId,
          typing: true,
        });
        break;
      case 'salesforce':
        // Send it right away, and then send it every 2 seconds. This is needed
        // because Salesforce automatically removes typing indicator after 3
        // seconds. It has to be 2 seconds and not 3 to avoid sending "started"
        // and "stopped" event at the same time.
        sendTypingIndicator(
          chatId,
          CONVERSATION_CONSTANTS.EntryTypes.TYPING_STARTED_INDICATOR,
        );
        clearInterval(typingInterval.current);
        typingInterval.current = setInterval(() => {
          sendTypingIndicator(
            chatId,
            CONVERSATION_CONSTANTS.EntryTypes.TYPING_STARTED_INDICATOR,
          );
        }, 2000);
        break;
    }
  };

  const handleTypingActivity = () => {
    if (!isTyping) {
      startTyping();
    }

    debouncedEndTyping();
  };

  const handleSuncoLiveChat = async () => {
    // Disregard typing errors
    try {
      Smooch.stopTyping();
    } catch {
      // Continue regardless of error
    }

    /**
     * Send the message to smooch
     *
     * widget component is added in the Smooch message:sent handler
     */
    try {
      await Smooch.sendMessage(liveChatQuery, chatId);
    } catch (error) {
      const errorMessage = 'Message failed to send';
      dispatch(addScreenReaderNotification(errorMessage));
      dispatch(addLiveChatEventMessage({ message: errorMessage }));
      sendErrorToSentry({
        conversationId: conversationId,
        error: `Sunco Live Chat failed to send message -- ${getSmoochErrorMessage(error)}`,
      });
    }
  };

  const onSubmit = () => {
    switch (liveChatIntegration) {
      case 'sunco':
        handleSuncoLiveChat();
        break;
      case 'salesforce':
        sendTextMessage(chatId, liveChatQuery, uuidv4())
          .then(() => {
            dispatch(sendUserReply({ message: liveChatQuery }));
          })
          .catch((err: unknown) => {
            const errorMessage = 'Message failed to send';
            dispatch(addScreenReaderNotification(errorMessage));
            dispatch(addLiveChatEventMessage({ message: errorMessage }));
            sendErrorToSentry({
              additionalContext: { chatId, message: liveChatQuery },
              conversationId: conversationId,
              error: `Salesforce One Chat failed to send message -- ${getErrorMessage(err)}`,
            });
          })
          .finally(() => {
            setLiveChatQuery('');
            return;
          });
        break;
      case 'zendesk':
        applyZopimFunction(widgetInitMethod, 'sendChatMsg', liveChatQuery);
        break;
      case 'kustomer':
        applyKustomerCoreFunctionWithCallback('sendMessage', {
          body: liveChatQuery,
          conversationId: chatId,
          isInAssistantMode,
        })
          .then(response => {
            dispatch(sendUserReply({ message: response.body || '' }));
          })
          .catch((err: unknown) => {
            const errorMessage = 'Message failed to send';
            dispatch(addScreenReaderNotification(errorMessage));
            dispatch(addLiveChatEventMessage({ message: errorMessage }));
            sendErrorToSentry({
              additionalContext: { chatId, message: liveChatQuery },
              conversationId: conversationId,
              error: `Kustomer One Chat failed to send message -- ${err}`,
            });
          })
          .finally(() => {
            dispatch(setLiveChatQuery(''));
            return;
          });
        break;
      default:
        dispatch(sendUserReply({ message: liveChatQuery }));
    }
    dispatch(addScreenReaderNotification('Message sent'));
    dispatch(setLiveChatQuery(''));
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setLiveChatQuery(e.target.value));
    handleTypingActivity();

    // if in smooch, send start/stop typing event here
    if (liveChatIntegration === 'sunco' && status === 'active') {
      // it's possible that Smooch is not fully init'ed when the end user is typing.
      // In this case typing functions would not be defined and an error would be thrown.
      try {
        if (e.target.value !== '') {
          Smooch.startTyping();
        } else {
          Smooch.stopTyping();
        }
      } catch {}
    }
  };

  return (
    <IntentFormWrapper
      formTitle=''
      isDisabled={isSubmitDisabled}
      isForm
      isLastStep={false}
      noValidate
      onSubmit={onSubmit}
      stepIndex={-1}
    >
      <TextInput
        aria-label={placeholder}
        dir='auto'
        disableSendMessage={isSubmitDisabled}
        InputProps={{
          inputProps: {
            className: 'forethought-scrollbar',
            'data-testid': 'live-chat-input',
            maxLength: getMaxLength(),
          },
          startAdornment: (
            <InputAdornment position='start'>
              <UploadAttachmentButton
                isDisabled={
                  status !== 'active' ||
                  isLatestStepZendeskCsat ||
                  userEndedChat
                }
                maxFileSize={getMaxFileSizeMegabytes()}
              />
            </InputAdornment>
          ),
        }}
        maxRows={10}
        multiline
        onChange={onChange}
        onSendMessage={onSubmit}
        placeholder={placeholder}
        sendButtonTooltipContent={
          isLatestStepZendeskCsat
            ? 'Conversation has been ended. You can rate your conversation.'
            : ''
        }
        value={liveChatQuery}
      />
    </IntentFormWrapper>
  );
};

export default LiveChatInput;
